'use client';

import { c as _c } from "react/compiler-runtime";
import type { ChainId } from '@morpho-org/blue-sdk';
import { createSerializer, useQueryStates } from 'nuqs';
import { useEffect, useTransition } from 'react';
import type { BaseMasterFilterState } from '@/helpers/filter/vaultMasterFilter/masterFilterState';
import { PageKey } from '@/helpers/cookieStorage/userSettings/types';
import { setUserSettingsCookie } from '@/helpers/cookieStorage/userSettings/userSettingsCookie.client';
import { baseStateToUrlParams, baseUrlParamsToState, DEFAULT_BASE_MASTER_FILTER_STATE } from '@/helpers/filter/vaultMasterFilter/masterFilterState';
import { sanitizeVaultsChainIds } from '../config/chainIds';
import { VaultsUrlFilterParser } from '../config/config';
export interface VaultsMasterFilterState extends BaseMasterFilterState {
  chains: ChainId[];
  v1: boolean;
}
type UrlParams = ReturnType<typeof useQueryStates<typeof VaultsUrlFilterParser>>[0];

// What a fresh visit resolves to, what the Advanced badge counts against, and what Reset
// restores. `v1` comes from the nuqs parser default in config.ts.
export const DEFAULT_MASTER_FILTER_STATE: VaultsMasterFilterState = {
  ...DEFAULT_BASE_MASTER_FILTER_STATE,
  chains: [],
  v1: VaultsUrlFilterParser.v1.defaultValue
};

// Keys promoted to the toolbar — excluded from the Advanced badge count.
export const VAULTS_MASTER_FILTER_EXCLUDE_KEYS: (keyof VaultsMasterFilterState)[] = ['chains', 'assets', 'curators'];

// `v1` never contributes to the analytics active_count, so the series stays comparable
// across triggers.
export const VAULTS_ANALYTICS_EXCLUDE_KEYS: (keyof VaultsMasterFilterState)[] = [...VAULTS_MASTER_FILTER_EXCLUDE_KEYS, 'v1'];
export function urlParamsToMasterFilterState(params: UrlParams): VaultsMasterFilterState {
  return {
    ...baseUrlParamsToState(params),
    // Unsupported ids are dropped so the network and asset filters reflect what the
    // list is actually showing, and the next filter change writes the clean set back.
    chains: sanitizeVaultsChainIds(params.chains),
    v1: params.v1
  };
}
export function masterFilterStateToUrlParams(state: VaultsMasterFilterState): Partial<UrlParams> {
  return {
    ...baseStateToUrlParams(state),
    // oxlint-disable-next-line typescript/consistent-type-assertions
    chains: state.chains as number[],
    v1: state.v1
  };
}
const serializeVaultsFilters = createSerializer(VaultsUrlFilterParser);
export function useVaultsMasterFilter() {
  const $ = _c(13);
  const [urlParams, setUrlParams] = useQueryStates(VaultsUrlFilterParser);
  const [isTransitionPending, startTransition] = useTransition();
  let t0;
  let t1;
  if ($[0] !== urlParams) {
    t0 = () => {
      if (window.location.pathname !== "/vaults") {
        return;
      }
      const qs = serializeVaultsFilters(urlParams);
      setUserSettingsCookie([PageKey.Earn, "filters"], qs || null);
    };
    t1 = [urlParams];
    $[0] = urlParams;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  useEffect(t0, t1);
  let t2;
  if ($[3] !== urlParams) {
    t2 = urlParamsToMasterFilterState(urlParams);
    $[3] = urlParams;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const masterFilterState = t2;
  let t3;
  if ($[5] !== setUrlParams) {
    t3 = newState => {
      startTransition(() => {
        setUrlParams(masterFilterStateToUrlParams(newState));
      });
    };
    $[5] = setUrlParams;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const onMasterFilterChange = t3;
  let t4;
  if ($[7] !== isTransitionPending || $[8] !== masterFilterState || $[9] !== onMasterFilterChange || $[10] !== setUrlParams || $[11] !== urlParams) {
    t4 = {
      urlParams,
      setUrlParams,
      masterFilterState,
      onMasterFilterChange,
      isTransitionPending,
      startTransition
    };
    $[7] = isTransitionPending;
    $[8] = masterFilterState;
    $[9] = onMasterFilterChange;
    $[10] = setUrlParams;
    $[11] = urlParams;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  return t4;
}